import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Banknote, X, Undo2, Clock, CircleAlert, Ban, HandCoins } from 'lucide-react';
import StatusBadge from '../../components/StatusBadge';
import { useToast } from '../../components/ToastProvider';
import { useAuth } from '../../context/AuthContext';
import ReversarPagoModal from '../../components/ReversarPagoModal';
import CondonarMoratorioModal, { MOTIVOS_CONDONACION } from '../../components/CondonarMoratorioModal';
import { creditosAPI, moratoriosAPI, describirError } from '../../services/api';
import { formatoMoneda } from '../../utils/finanzas';
import '../../styles/Pages.css';
import '../../styles/Expediente.css';
import '../../styles/FormModal.css';
import '../../styles/PagosTab.css';

const moneda = formatoMoneda;

const fechaLarga = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' }).toUpperCase();
};

const fechaHoy = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Mexico_City' }).format(new Date());

const FORMAS_PAGO = [
  { value: 'EFECTIVO', label: 'Efectivo' },
  { value: 'TRANSFERENCIA', label: 'Transferencia' },
  { value: 'DEPOSITO', label: 'Depósito' },
  { value: 'OTRO', label: 'Otro' }
];

const ESTADOS_COBRABLES = ['AUTORIZADO', 'ACTIVO', 'LIQUIDADO'];

/**
 * Pestaña Pagos del expediente de crédito (Fase 7.0, extendida en Fase
 * 8.0 con moratorios). Toda la aritmética — saldos, reparto entre cuotas o
 * cargos, cobertura, vencimiento, D+3 — viene del backend (sección 33 de
 * Fase 7.0, vigente: el frontend no calcula nada, sólo pide y pinta).
 */
function PagosTab({ credito, onCreditoActualizado }) {
  const toast = useToast();
  const { hasPermission } = useAuth();
  const puedeRegistrarPago = hasPermission('PAGOS_REGISTRAR');
  const puedeReversar = hasPermission('PAGOS_REVERSAR');
  const puedeCondonar = hasPermission('MORATORIOS_CONDONAR');
  const puedeCancelarMoratorio = hasPermission('MORATORIOS_CANCELAR');

  const [saldo, setSaldo] = useState(null);
  const [pagos, setPagos] = useState([]);
  const [moratorios, setMoratorios] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);

  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [pagoAReversar, setPagoAReversar] = useState(null);
  const [pagoEnDetalle, setPagoEnDetalle] = useState(null);
  const [cargoACancelar, setCargoACancelar] = useState(null);
  const [cargoACondonar, setCargoACondonar] = useState(null);
  const [resumenMoratorios, setResumenMoratorios] = useState(null);
  const [condonaciones, setCondonaciones] = useState([]);

  const cargar = useCallback(async () => {
    setCargando(true);
    setError(null);
    try {
      const [s, p, m] = await Promise.all([
        creditosAPI.saldo(credito.id),
        creditosAPI.listarPagos(credito.id),
        creditosAPI.listarMoratorios(credito.id)
      ]);
      setSaldo(s.data);
      setPagos(p.data.pagos);
      // Efectivos = persistidos + virtuales a la fecha operativa (P08-B0): un
      // moratorio ya exigible aparece aunque todavía no se haya materializado.
      setMoratorios(m.data.moratoriosEfectivos ?? m.data.moratorios);
      setResumenMoratorios(m.data.resumen ?? null);
      setCondonaciones(m.data.condonaciones ?? []);
    } catch (e) {
      setError(describirError(e));
    } finally {
      setCargando(false);
    }
  }, [credito.id]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  const refrescarTrasMovimiento = async () => {
    await cargar();
    // El estado del crédito puede haber cambiado (ACTIVO/LIQUIDADO/AUTORIZADO
    // tras una reversa o cancelación, secciones 14/36): el header del
    // expediente necesita saberlo sin que el usuario recargue la página.
    try {
      const { data } = await creditosAPI.obtener(credito.id);
      onCreditoActualizado?.(data);
    } catch {
      /* el refresco del header es una mejora, no crítico si falla */
    }
  };

  const puedeRecibirPagos = ESTADOS_COBRABLES.includes(credito.estado);
  const hayMoratoriosExigibles = saldo && Number(saldo.saldoMoratorios) > 0;

  if (cargando) {
    return <div className="loading">Cargando pagos…</div>;
  }

  if (error) {
    return (
      <div className="estado-vacio">
        <span className="estado-vacio-icono is-error"><CircleAlert size={20} strokeWidth={1.75} /></span>
        <p className="estado-vacio-texto">{error.mensaje}</p>
        <button className="btn btn-secondary" onClick={cargar}>Reintentar</button>
      </div>
    );
  }

  const proxima = saldo?.proximaCuota;

  return (
    <div className="exp-panel pagos-tab">
      {/* Saldo de cuotas / Moratorios / Saldo exigible (Fase 8.0, sección 22) */}
      <div className="pagos-saldos-grid">
        <div className="pagos-saldo-tile">
          <span className="pagos-resumen-etiqueta">Saldo de cuotas</span>
          <span className="pagos-saldo-valor">{moneda(saldo?.saldoCuotas)}</span>
        </div>
        <div className={`pagos-saldo-tile ${hayMoratoriosExigibles ? 'is-alerta' : ''}`}>
          <span className="pagos-resumen-etiqueta">Moratorios</span>
          <span className="pagos-saldo-valor">{moneda(saldo?.saldoMoratorios)}</span>
        </div>
        <div className="pagos-saldo-tile is-total">
          <span className="pagos-resumen-etiqueta">Saldo exigible</span>
          <span className="pagos-saldo-valor">{moneda(saldo?.saldoExigible)}</span>
        </div>
      </div>

      <div className="pagos-resumen">
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Próxima cuota</span>
          <span className="pagos-resumen-valor">{proxima ? `#${proxima.numeroPago}` : '—'}</span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Fecha de vencimiento</span>
          <span className="pagos-resumen-valor">{proxima ? fechaLarga(proxima.fechaVencimiento) : '—'}</span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Importe cuota</span>
          <span className="pagos-resumen-valor">{proxima ? moneda(proxima.importeProgramado) : '—'}</span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Importe aplicado</span>
          <span className="pagos-resumen-valor">{proxima ? moneda(proxima.importeAplicado) : '—'}</span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Importe pendiente</span>
          <span className="pagos-resumen-valor">{proxima ? moneda(proxima.importePendiente) : '—'}</span>
        </div>
        <div className="pagos-resumen-item">
          <span className="pagos-resumen-etiqueta">Condición</span>
          <span className="pagos-resumen-valor">
            {proxima ? (
              <>
                <StatusBadge value={proxima.cobertura} /> <StatusBadge value={proxima.vencimiento} />
              </>
            ) : (
              <StatusBadge value={saldo?.estadoCredito === 'LIQUIDADO' ? 'LIQUIDADO' : '—'} />
            )}
          </span>
        </div>
      </div>

      {/*
        Gateado también en hayMoratoriosExigibles (I-53): `proxima.moratorioAplicable` sólo significa "la
        cuota ya venció" — es informativo, no confirma que el backend ya considere exigible un cargo (D+3,
        fechaCorteLegacy). `hayMoratoriosExigibles` viene de saldo.saldoMoratorios, la misma cifra autoritativa
        que ya se muestra arriba, calculada por moratoriosService (incluye D+3 y la protección de corte legacy).
        No se duplica ningún cálculo de moratorio aquí — sólo se reutiliza uno que el backend ya envió.
      */}
      {proxima?.moratorioAplicable && hayMoratoriosExigibles && (
        <div className="data-notice">
          <CircleAlert size={16} strokeWidth={1.75} />
          <span>
            La cuota #{proxima.numeroPago} está vencida y el crédito tiene moratorio exigible:{' '}
            <strong>{moneda(saldo.saldoMoratorios)}</strong>. Ver detalle abajo.
          </span>
        </div>
      )}

      <div className="exp-panel-head pagos-panel-head">
        <div>
          <h3>Pagos</h3>
          <p>{pagos.length} pago(s) registrado(s).</p>
        </div>
        <button
          className="btn btn-primary"
          onClick={() => setMostrarFormulario(true)}
          disabled={!puedeRecibirPagos || !puedeRegistrarPago}
          title={!puedeRegistrarPago ? 'Sin permiso para registrar pagos: lo verifica y registra administración' : (!puedeRecibirPagos ? `Un crédito en estado ${credito.estado} no admite pagos` : undefined)}
        >
          <Banknote size={16} strokeWidth={1.75} />
          Registrar pago
        </button>
      </div>

      {pagos.length === 0 ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono"><Clock size={20} strokeWidth={1.75} /></span>
          <p className="estado-vacio-texto">Sin pagos registrados todavía.</p>
        </div>
      ) : (
        <div className="datatable-wrapper">
          <table className="datatable">
            <thead>
              <tr>
                <th>Folio</th>
                <th>Fecha de pago</th>
                <th>Importe</th>
                <th>Forma</th>
                <th>Estado</th>
                <th>Referencia</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {pagos.map((p) => (
                <tr key={p.id} className={p.estado === 'REVERSADO' ? 'is-reversado' : undefined}>
                  <td>{p.folioPago}</td>
                  <td>{fechaLarga(p.fechaPago)}</td>
                  <td>{moneda(p.importeRecibido)}</td>
                  <td>{FORMAS_PAGO.find((f) => f.value === p.formaPago)?.label || p.formaPago}</td>
                  <td><StatusBadge value={p.estado} /></td>
                  <td>{p.referencia || <span className="cell-muted">—</span>}</td>
                  <td className="pagos-acciones">
                    <button className="btn btn-ghost btn-sm" onClick={() => setPagoEnDetalle(p)}>Ver</button>
                    {puedeReversar && p.estado === 'APLICADO' && p.reversible !== false && (
                      <button className="btn btn-ghost btn-sm is-peligro" onClick={() => setPagoAReversar(p)}>
                        <Undo2 size={14} strokeWidth={1.75} />
                        Reversar pago
                      </button>
                    )}
                    {puedeReversar && p.estado === 'APLICADO' && p.reversible === false && (
                      <span className="cell-muted" data-testid="pago-no-reversible" title="Pago histórico migrado o incluido en un cierre de caja cerrado: no se puede reversar." style={{ fontSize: '0.75rem', marginLeft: 6 }}>
                        No reversible
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Historial de moratorios generados (Fase 8.0, sección 23) */}
      <div className="exp-panel-head pagos-panel-head">
        <div>
          <h3>Moratorios</h3>
          <p>{moratorios.length} cargo(s) generado(s).</p>
        </div>
      </div>

      {resumenMoratorios && (
        <div className="pagos-saldos-grid">
          <div className="pagos-saldo-tile"><span className="pagos-resumen-etiqueta">Generado</span><span className="pagos-saldo-valor">{moneda(resumenMoratorios.generado)}</span></div>
          <div className="pagos-saldo-tile"><span className="pagos-resumen-etiqueta">Cobrado</span><span className="pagos-saldo-valor">{moneda(resumenMoratorios.cobrado)}</span></div>
          <div className="pagos-saldo-tile"><span className="pagos-resumen-etiqueta">Condonado</span><span className="pagos-saldo-valor">{moneda(resumenMoratorios.condonado)}</span></div>
          <div className={`pagos-saldo-tile ${Number(resumenMoratorios.pendiente) > 0 ? 'is-alerta' : ''}`}><span className="pagos-resumen-etiqueta">Pendiente</span><span className="pagos-saldo-valor">{moneda(resumenMoratorios.pendiente)}</span></div>
        </div>
      )}

      {moratorios.length === 0 ? (
        <div className="estado-vacio">
          <span className="estado-vacio-icono"><Clock size={20} strokeWidth={1.75} /></span>
          <p className="estado-vacio-texto">Sin cargos moratorios generados todavía.</p>
        </div>
      ) : (
        <div className="datatable-wrapper">
          <table className="datatable">
            <thead>
              <tr>
                <th>Cuota</th>
                <th>Vencimiento</th>
                <th>Generación</th>
                <th>Base</th>
                <th>10%</th>
                <th>Importe</th>
                <th>Cobrado</th>
                <th>Condonado</th>
                <th>Pendiente</th>
                <th>Estado</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {moratorios.map((m) => (
                <tr key={m.id || `virtual-${m.cuotaId}`} className={m.estado === 'CANCELADO' ? 'is-reversado' : undefined}>
                  <td>#{m.numeroPago}</td>
                  <td>{fechaLarga(m.fechaVencimientoCuota)}</td>
                  <td>{m.virtual ? <span className="cell-muted" title="Exigible; se registra al primer pago o condonación">Exigible (por registrar)</span> : fechaLarga(m.fechaGeneracion)}</td>
                  <td>{moneda(m.baseCalculo)}</td>
                  <td>{(Number(m.porcentaje) * 100).toFixed(0)}%</td>
                  <td>{moneda(m.importeOriginal)}</td>
                  <td>{moneda(m.importeAplicado)}</td>
                  <td>{moneda(m.importeCondonado)}</td>
                  <td>{moneda(m.importePendiente)}</td>
                  <td><StatusBadge value={m.estado} /></td>
                  <td className="pagos-acciones">
                    {puedeCondonar && m.estado !== 'CANCELADO' && Number(m.importePendiente) > 0 && (
                      <button className="btn btn-ghost btn-sm" onClick={() => setCargoACondonar(m)}>
                        <HandCoins size={14} strokeWidth={1.75} />
                        Condonar moratorio
                      </button>
                    )}
                    {puedeCancelarMoratorio && m.id && !m.virtual && m.estado === 'PENDIENTE' && (
                      <button className="btn btn-ghost btn-sm is-peligro" onClick={() => setCargoACancelar(m)}>
                        <Ban size={14} strokeWidth={1.75} />
                        Cancelar
                      </button>
                    )}
                    {m.canceladoEn && (
                      <span className="cell-muted" title={m.motivoCancelacion}>
                        {m.canceladoPor} · {fechaLarga(m.canceladoEn)}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {condonaciones.length > 0 && (
        <>
          <div className="exp-panel-head pagos-panel-head">
            <div>
              <h3>Condonaciones de moratorio</h3>
              <p>{condonaciones.length} condonación(es) registrada(s). Lo condonado no es cobro.</p>
            </div>
          </div>
          <div className="datatable-wrapper">
            <table className="datatable">
              <thead>
                <tr><th>Movimiento</th><th>Cuota</th><th>Importe</th><th>Pendiente después</th><th>Actor</th><th>Motivo</th><th>Fecha</th></tr>
              </thead>
              <tbody>
                {condonaciones.map((c) => (
                  <tr key={c.id}>
                    <td>CONDONACIÓN DE MORATORIO <span className="cell-muted">({c.tipo === 'TOTAL' ? 'total' : 'parcial'})</span></td>
                    <td>#{c.numeroPago}</td>
                    <td>{moneda(c.importeCondonado)}</td>
                    <td>{moneda(c.importePendienteDespues)}</td>
                    <td>{c.autorizadoPorNombre || c.autorizadoPor}{c.autorizadoPorRol ? <span className="cell-muted"> · {c.autorizadoPorRol}</span> : null}</td>
                    <td title={c.observaciones || undefined}>
                      {MOTIVOS_CONDONACION.find((x) => x.value === c.motivoCodigo)?.label || c.motivoCodigo}
                      {c.observaciones ? <span className="cell-muted"> · {c.observaciones}</span> : null}
                    </td>
                    <td>{new Date(c.fecha).toLocaleString('es-MX', { timeZone: 'America/Mexico_City' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {mostrarFormulario && (
        <RegistrarPagoModal
          credito={credito}
          saldo={saldo}
          onClose={() => setMostrarFormulario(false)}
          onRegistrado={async () => {
            setMostrarFormulario(false);
            await refrescarTrasMovimiento();
            toast.exito('Pago registrado.');
          }}
        />
      )}

      {pagoAReversar && (
        <ReversarPagoModal
          pago={pagoAReversar}
          folioCredito={credito.folio}
          onClose={() => setPagoAReversar(null)}
          onReversado={async () => {
            setPagoAReversar(null);
            await refrescarTrasMovimiento();
            toast.exito('Pago reversado.');
          }}
        />
      )}

      {pagoEnDetalle && (
        <DetallePagoModal pago={pagoEnDetalle} onClose={() => setPagoEnDetalle(null)} />
      )}

      {cargoACondonar && (
        <CondonarMoratorioModal
          creditoId={credito.id}
          cargo={cargoACondonar}
          onClose={() => setCargoACondonar(null)}
          onCondonado={async () => {
            setCargoACondonar(null);
            await refrescarTrasMovimiento();
            toast.exito('Moratorio condonado.');
          }}
        />
      )}

      {cargoACancelar && (
        <CancelarMoratorioModal
          cargo={cargoACancelar}
          onClose={() => setCargoACancelar(null)}
          onCancelado={async () => {
            setCargoACancelar(null);
            await refrescarTrasMovimiento();
            toast.exito('Cargo moratorio cancelado.');
          }}
        />
      )}
    </div>
  );
}

/**
 * Formulario de registro con previsualización en vivo (sección 33 de Fase
 * 7.0). El debounce evita llamar al backend en cada tecla; el
 * AbortController evita que una respuesta vieja pinte encima de una más
 * nueva.
 *
 * Prelación automática (P08-B0): el cajero no elige destino. El backend
 * aplica cuota vencida -> su moratorio -> siguientes cuotas, y la
 * previsualización muestra exactamente ese reparto.
 */
function RegistrarPagoModal({ credito, saldo, onClose, onRegistrado }) {
  const toast = useToast();
  const [fechaPago, setFechaPago] = useState(fechaHoy());
  const [importeRecibido, setImporteRecibido] = useState('');
  const [formaPago, setFormaPago] = useState('EFECTIVO');
  const [referencia, setReferencia] = useState('');
  const [observaciones, setObservaciones] = useState('');

  const [previa, setPrevia] = useState(null);
  const [previaError, setPreviaError] = useState(null);
  const [previsualizando, setPrevisualizando] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const abortRef = useRef(null);
  const idempotencyKeyRef = useRef(`ui-${credito.id}-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const hayMoratoriosExigibles = Number(saldo?.saldoMoratorios) > 0;

  useEffect(() => {
    setPrevia(null);
    setPreviaError(null);

    const importeValido = /^\d+(\.\d{1,2})?$/.test(importeRecibido) && Number(importeRecibido) > 0;
    if (!importeValido || !fechaPago) return;

    const timer = setTimeout(async () => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;
      setPrevisualizando(true);
      try {
        const { data } = await creditosAPI.previsualizarPago(
          credito.id,
          { fechaPago, importeRecibido },
          { signal: controller.signal }
        );
        setPrevia(data);
        setPreviaError(null);
      } catch (e) {
        if (e.code === 'ERR_CANCELED' || e.name === 'CanceledError') return;
        setPrevia(null);
        setPreviaError(describirError(e));
      } finally {
        setPrevisualizando(false);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [credito.id, fechaPago, importeRecibido]);

  const puedeEnviar = useMemo(
    () => !!previa && !previaError && !enviando,
    [previa, previaError, enviando]
  );

  const enviar = async (e) => {
    e.preventDefault();
    if (!puedeEnviar) return;
    setEnviando(true);
    try {
      await creditosAPI.registrarPago(credito.id, {
        fechaPago,
        importeRecibido,
        formaPago,
        referencia: referencia || undefined,
        observaciones: observaciones || undefined,
        idempotencyKey: idempotencyKeyRef.current
      });
      await onRegistrado();
    } catch (e2) {
      toast.error(describirError(e2).mensaje);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Registrar pago</h2>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <form className="modal-form" onSubmit={enviar}>
          {hayMoratoriosExigibles && (
            <div className="data-notice">
              <CircleAlert size={16} strokeWidth={1.75} />
              <span>
                Aplicación automática: primero la cuota vencida, después su moratorio
                ({moneda(saldo.saldoMoratorios)} pendiente) y luego las siguientes cuotas.
              </span>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="importeRecibido">Importe recibido</label>
            <input
              id="importeRecibido"
              type="text"
              inputMode="decimal"
              placeholder="0.00"
              value={importeRecibido}
              onChange={(e) => setImporteRecibido(e.target.value.replace(',', '.'))}
              required
              autoFocus
            />
          </div>

          <div className="form-group">
            <label htmlFor="fechaPago">Fecha de pago</label>
            <input id="fechaPago" type="date" value={fechaPago} onChange={(e) => setFechaPago(e.target.value)} required />
          </div>

          <div className="form-group">
            <label htmlFor="formaPago">Forma de pago</label>
            <select id="formaPago" value={formaPago} onChange={(e) => setFormaPago(e.target.value)}>
              {FORMAS_PAGO.map((f) => (
                <option key={f.value} value={f.value}>{f.label}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="referencia">Referencia (opcional)</label>
            <input id="referencia" type="text" value={referencia} onChange={(e) => setReferencia(e.target.value)} />
          </div>

          <div className="form-group">
            <label htmlFor="observaciones">Observaciones (opcional)</label>
            <textarea id="observaciones" value={observaciones} onChange={(e) => setObservaciones(e.target.value)} />
          </div>

          <div className="pagos-previsualizacion">
            <h4>Aplicación prevista</h4>
            {previsualizando && <p className="cell-muted">Calculando…</p>}
            {previaError && <p className="pagos-previsualizacion-error">{previaError.mensaje}</p>}
            {previa && !previsualizando && (
              <>
                <table className="datatable datatable-compacta">
                  <thead>
                    <tr><th>Destino</th><th>Importe aplicado</th></tr>
                  </thead>
                  <tbody>
                    {previa.aplicaciones.map((a, i) => (
                      <tr key={`${a.tipo}-${a.cuotaId || a.cargoId}-${i}`}>
                        <td>{a.tipo === 'MORATORIO' ? `Moratorio cuota #${a.numeroPago}` : `Cuota #${a.numeroPago}`}</td>
                        <td>{moneda(a.importeAplicado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="pagos-previsualizacion-totales">
                  <span>Saldo antes: {moneda(previa.saldoAntes)}</span>
                  <span>Saldo después: <strong>{moneda(previa.saldoDespues)}</strong></span>
                </div>
              </>
            )}
            {!previa && !previsualizando && !previaError && (
              <p className="cell-muted">Escribe un importe para ver cómo se aplicaría.</p>
            )}
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={!puedeEnviar}>
              {enviando ? 'Registrando…' : 'Registrar pago'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CancelarMoratorioModal({ cargo, onClose, onCancelado }) {
  const toast = useToast();
  const [motivo, setMotivo] = useState('');
  const [enviando, setEnviando] = useState(false);

  const enviar = async (e) => {
    e.preventDefault();
    if (motivo.trim().length < 3) return;
    setEnviando(true);
    try {
      await moratoriosAPI.cancelar(cargo.id, motivo.trim());
      await onCancelado();
    } catch (e2) {
      toast.error(describirError(e2).mensaje);
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Cancelar moratorio de la cuota #{cargo.numeroPago}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <form className="modal-form" onSubmit={enviar}>
          <div className="data-notice">
            <CircleAlert size={16} strokeWidth={1.75} />
            <span>
              El cargo queda visible en el historial como CANCELADO, con tu motivo. Sólo se puede
              cancelar un cargo sin pagos aplicados — si ya tiene pagos, reviértelos primero.
            </span>
          </div>

          <div className="form-group">
            <label htmlFor="motivoCancelacion">Motivo (obligatorio)</label>
            <textarea
              id="motivoCancelacion"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              required
              minLength={3}
              autoFocus
            />
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
            <button type="submit" className="btn btn-danger" disabled={motivo.trim().length < 3 || enviando}>
              {enviando ? 'Cancelando…' : 'Confirmar cancelación'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function DetallePagoModal({ pago, onClose }) {
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Pago {pago.folioPago}</h2>
          <button className="modal-close" onClick={onClose} aria-label="Cerrar">
            <X size={18} strokeWidth={1.75} />
          </button>
        </div>

        <div className="modal-form">
          <dl className="pagos-detalle-lista">
            <div><dt>Fecha de pago</dt><dd>{fechaLarga(pago.fechaPago)}</dd></div>
            <div><dt>Importe recibido</dt><dd>{moneda(pago.importeRecibido)}</dd></div>
            <div><dt>Forma de pago</dt><dd>{pago.formaPago}</dd></div>
            <div><dt>Estado</dt><dd><StatusBadge value={pago.estado} /></dd></div>
            <div><dt>Referencia</dt><dd>{pago.referencia || '—'}</dd></div>
            <div><dt>Observaciones</dt><dd>{pago.observaciones || '—'}</dd></div>
          </dl>

          <h4>Aplicaciones</h4>
          <table className="datatable datatable-compacta">
            <thead><tr><th>Destino</th><th>Importe aplicado</th></tr></thead>
            <tbody>
              {pago.aplicaciones.map((a) => (
                <tr key={a.id}>
                  <td>{a.tipo === 'MORATORIO' ? `Moratorio cuota #${a.numeroPago}` : `Cuota #${a.numeroPago}`}</td>
                  <td>{moneda(a.importeAplicado)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {pago.reversa && (
            <div className="data-notice is-error">
              <CircleAlert size={16} strokeWidth={1.75} />
              <span>
                Reversado por {pago.reversa.reversadoPor} el {fechaLarga(pago.reversa.createdAt)}.
                Motivo: {pago.reversa.motivo}
              </span>
            </div>
          )}

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cerrar</button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PagosTab;
